import { useInView, useReducedMotion } from "motion/react";
import { ArrowDown, BookOpenText, Lightbulb, MessagesSquare, ScanSearch, SquarePen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { STEPS } from "@/data/content";
import { cn } from "@/lib/utils";

const STEP_MS = 2600; // how long each step of the loop stays lit
const N = STEPS.length;
const MAX_RULES = 5; // the little skill card starts over after this many laps
const ICONS = [ScanSearch, MessagesSquare, Lightbulb, SquarePen];
const RULE_WIDTHS = ["78%", "94%", "62%", "86%", "70%"];

// Ring geometry, in SVG units. Steps sit at 12, 3, 6 and 9 o'clock.
const C = 240;
const R = 180;
const QUARTER = (2 * Math.PI * R) / N;
const radians = (i: number) => ((-90 + (i * 360) / N) * Math.PI) / 180;
const arc = (i: number) => {
  const [a, b] = [radians(i), radians(i + 1)];
  return `M ${C + R * Math.cos(a)} ${C + R * Math.sin(a)} A ${R} ${R} 0 0 1 ${C + R * Math.cos(b)} ${C + R * Math.sin(b)}`;
};
/** Where a step sits, as a percentage of the ring's box. */
const spot = (i: number) => ({
  left: `${50 + (R / (2 * C)) * 100 * Math.cos(radians(i))}%`,
  top: `${50 + (R / (2 * C)) * 100 * Math.sin(radians(i))}%`,
});

function Logo() {
  return (
    <svg viewBox="0 0 32 32" className="size-7" aria-hidden>
      <rect width="32" height="32" rx="9" fill="var(--color-ink)" />
      <path d="M22.5 16a6.5 6.5 0 1 1-2.3-4.96" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M20.6 6.9v4.6h-4.6" fill="none" stroke="#8f86ff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Ring({
  step,
  rules,
  running,
  tick,
  onPick,
}: {
  step: number;
  rules: number;
  running: boolean;
  tick: number;
  onPick: (i: number) => void;
}) {
  const updating = step === N - 1;

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[520px] short:max-w-[min(520px,66vh)]">
      <svg viewBox="0 0 480 480" className="absolute inset-0 size-full" aria-hidden>
        <defs>
          <linearGradient id="ring-colour" gradientUnits="userSpaceOnUse" x1="60" y1="60" x2="420" y2="420">
            <stop offset="0" stopColor="#5b4fe9" />
            <stop offset="0.55" stopColor="#c04fe0" />
            <stop offset="1" stopColor="#ff8a4c" />
          </linearGradient>
        </defs>

        <circle cx={C} cy={C} r={R} fill="none" stroke="var(--color-line-2)" strokeWidth="2" />

        {/* the part of the loop already travelled this lap */}
        {STEPS.map((s, i) =>
          i < step ? (
            <path key={s.key} d={arc(i)} fill="none" stroke="url(#ring-colour)" strokeWidth="4" strokeLinecap="round" />
          ) : null,
        )}

        {/* the part being travelled now */}
        <path
          key={`arc-${tick}-${running}`}
          d={arc(step)}
          fill="none"
          stroke="url(#ring-colour)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={QUARTER}
          style={
            running
              ? ({ "--arc-length": QUARTER, animation: `arc-progress ${STEP_MS}ms linear forwards` } as React.CSSProperties)
              : { strokeDashoffset: QUARTER }
          }
        />

        {/* the case moving around the loop */}
        <g
          key={`dot-${tick}-${running}`}
          style={
            {
              transformOrigin: `${C}px ${C}px`,
              "--from": `${step * 90}deg`,
              "--to": `${(step + 1) * 90}deg`,
              transform: `rotate(${step * 90}deg)`,
              animation: running ? `orbit ${STEP_MS}ms linear forwards` : undefined,
            } as React.CSSProperties
          }
        >
          <circle cx={C} cy={C - R} r="13" fill="#5b4fe9" opacity="0.16" />
          <circle cx={C} cy={C - R} r="7" fill="#fff" stroke="#5b4fe9" strokeWidth="3.5" />
        </g>
      </svg>

      {/* the four steps */}
      {STEPS.map((s, i) => {
        const Icon = ICONS[i]!;
        const on = i === step;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onPick(i)}
            aria-pressed={on}
            style={spot(i)}
            className={cn(
              "absolute flex h-9 -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-full border pr-3 pl-2.5 text-[13px] font-medium whitespace-nowrap transition-all duration-500 sm:h-11 sm:gap-2 sm:pr-4 sm:pl-3 sm:text-[15px]",
              on
                ? "scale-110 border-ink bg-ink text-white shadow-[0_14px_30px_-12px_rgb(16_16_20/0.6)]"
                : "border-line bg-card text-ink-2 hover:text-ink",
            )}
          >
            <Icon aria-hidden className="size-4 sm:size-[18px]" strokeWidth={2} />
            {s.name}
          </button>
        );
      })}

      {/* the thing that evolves */}
      <div className="absolute top-1/2 left-1/2 w-[40%] min-w-[128px] -translate-x-1/2 -translate-y-1/2 sm:w-[44%]">
        {updating && (
          <span
            key={`ripple-${tick}`}
            aria-hidden
            className="absolute inset-0 rounded-[24px] border-2 border-skill"
            style={{ animation: "ripple 1.2s ease-out both" }}
          />
        )}
        <div className="card relative p-3 sm:p-5">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-[14px] font-semibold tracking-[-0.01em] sm:gap-2 sm:text-[16px]">
              <BookOpenText aria-hidden className="size-[18px] text-skill" />
              Skill
            </span>
            <span key={rules} className="pop rounded-full bg-skill px-2.5 py-0.5 font-mono text-[11px] text-white">
              v{rules}
            </span>
          </div>
          <div className="mt-3 space-y-[5px] sm:mt-4 sm:space-y-[7px]" aria-hidden>
            <div className="h-[5px] w-[88%] rounded-full bg-line-2 sm:h-[7px]" />
            {RULE_WIDTHS.map((width, i) => (
              <div
                key={i}
                className="h-[5px] rounded-full bg-skill transition-[width,opacity] duration-700 ease-out sm:h-[7px]"
                style={{ width: i < rules ? width : 0, opacity: i < rules ? 1 : 0 }}
              />
            ))}
          </div>
          <p className="mt-4 hidden text-[12.5px] leading-snug text-ink-3 sm:block">
            {rules === 0 ? "The playbook the AI follows." : `${rules} new ${rules === 1 ? "rule" : "rules"} learned.`}
          </p>
        </div>
      </div>
    </div>
  );
}

export function Goal() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-20% 0px -20% 0px" });
  const reduced = useReducedMotion();
  const [tick, setTick] = useState(0);
  const running = inView && !reduced;

  useEffect(() => {
    if (!running) return;
    const id = setTimeout(() => setTick(t => t + 1), STEP_MS);
    return () => clearTimeout(id);
  }, [tick, running]);

  const step = tick % N;
  const lap = Math.floor(tick / N);
  // One rule is added each time the loop reaches "Update".
  const rules = (lap % MAX_RULES) + (step === N - 1 ? 1 : 0);
  const pick = (i: number) => setTick(t => t - (t % N) + i);

  return (
    <section id="goal" className="relative flex min-h-[100svh] snap-start flex-col overflow-hidden">
      <div aria-hidden className="dots pointer-events-none absolute inset-0" />
      <div aria-hidden className="pointer-events-none absolute top-[6%] right-[-6%] h-[720px] w-[820px] max-w-none">
        <div
          className="absolute top-[8%] left-[18%] size-[380px] rounded-full bg-[#cfc8ff] opacity-70 blur-[90px]"
          style={{ animation: "float 16s ease-in-out infinite" }}
        />
        <div
          className="absolute top-[38%] left-[46%] size-[340px] rounded-full bg-[#ffc9de] opacity-60 blur-[90px]"
          style={{ animation: "float 19s ease-in-out infinite reverse" }}
        />
        <div
          className="absolute top-[52%] left-[10%] size-[300px] rounded-full bg-[#ffe0b5] opacity-60 blur-[90px]"
          style={{ animation: "float 22s ease-in-out infinite" }}
        />
      </div>

      <header className="shell relative flex h-[72px] shrink-0 items-center justify-between short:h-[58px]">
        <a href="#goal" className="flex items-center gap-2.5 text-[17px] font-semibold tracking-[-0.015em]">
          <Logo />
          Chimera RSI
        </a>
        <span className="text-[14px] text-ink-2">
          by <span className="font-medium text-ink">Fenz.AI</span>
        </span>
      </header>

      <div
        ref={ref}
        className="shell relative grid flex-1 grid-cols-1 content-center items-center gap-x-12 gap-y-14 pt-6 pb-16 short:pt-1 short:pb-5 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)]"
      >
        <div>
          <span className="tag inline-flex rounded-full border border-line bg-card px-3 py-1.5">
            The goal of this research
          </span>
          <h1 className="mt-6 text-[clamp(40px,min(5.1vw,8.4vh),74px)] leading-[1.0] font-semibold tracking-[-0.045em] text-balance short:mt-4">
            An AI code auditor that <span className="text-skill">learns</span> from every bug.
          </h1>
          <p className="mt-6 max-w-[540px] text-[clamp(16px,1.3vw,19px)] leading-[1.55] text-ink-2 short:mt-4 short:leading-[1.45]">
            Chimera reads code and looks for security bugs. RSI is the loop that makes it better each round. The AI
            model never changes. Only its <span className="font-semibold text-ink">skill</span> does: the playbook it
            follows.
          </p>

          <ol className="mt-7 max-w-[560px] short:mt-4">
            {STEPS.map((s, i) => {
              const on = i === step;
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    onClick={() => pick(i)}
                    aria-current={on ? "step" : undefined}
                    className={cn(
                      "relative flex w-full items-baseline gap-4 overflow-hidden rounded-2xl px-4 py-3 text-left transition-colors duration-300 short:py-2",
                      on ? "bg-card shadow-[0_1px_2px_rgb(16_16_20/0.05),0_10px_24px_-16px_rgb(16_16_20/0.25)]" : "hover:bg-card/70",
                    )}
                  >
                    <span className={cn("font-mono text-[12px]", on ? "text-skill" : "text-ink-3")}>0{i + 1}</span>
                    <span className="text-[16.5px] leading-snug">
                      <span className={cn("font-semibold", on ? "text-ink" : "text-ink-2")}>{s.name}</span>
                      <span className={cn("ml-2.5", on ? "text-ink-2" : "text-ink-3")}>{s.line}</span>
                    </span>
                    {on && running && (
                      <span
                        key={tick}
                        aria-hidden
                        className="absolute bottom-0 left-0 h-[2px] bg-skill"
                        style={{ animation: `bar ${STEP_MS}ms linear forwards` }}
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ol>

          <a href="#evolve" className="btn btn-dark mt-8 short:mt-5 short:h-11">
            Watch the skill evolve <ArrowDown aria-hidden className="size-[18px]" />
          </a>
        </div>

        <Ring step={step} rules={rules} running={running} tick={tick} onPick={pick} />
      </div>
    </section>
  );
}
